import { useEffect, useState } from 'react';

/**
 * Hands keyboard focus back to Retry when a retry fails again.
 *
 * Most pages swap a failed section for their loading state while it retries,
 * which destroys the Retry that was pressed. The component that owns the load
 * holds one of these, so the hand-off lives exactly as long as that page does.
 * A press arms it for its surface; the load settling marks the attempt
 * finished; the error surface that mounts in the owner's next render (the
 * retry failed again) claims focus. Anything else retires the attempt: the
 * owner rendering without a claim (the retry succeeded), the user moving focus
 * or pressing anywhere while it ran, or the owner unmounting (navigation).
 */
export interface RetryFocus {
  /** The user pressed Retry on `surface`; any earlier attempt is abandoned. */
  readonly arm: (surface: string) => void;
  /** The pressed retry's load has settled, successfully or not. */
  readonly settle: (surface: string) => void;
  /** True once, for the surface whose finished retry failed again. */
  readonly claim: (surface: string) => boolean;
  /** Drops a finished attempt nobody claimed. */
  readonly retireSettled: () => void;
  /** Drops any attempt. */
  readonly cancel: () => void;
}

type Attempt = { readonly surface: string; readonly settled: boolean };

const USER_FOCUS_EVENTS = ['focusin', 'pointerdown'] as const;

function createRetryFocus(): RetryFocus {
  let attempt: Attempt | null = null;

  function cancel(): void {
    attempt = null;
    for (const type of USER_FOCUS_EVENTS) {
      document.removeEventListener(type, cancel, true);
    }
  }

  return {
    arm(surface) {
      cancel();
      attempt = { surface, settled: false };
      // The user taking focus anywhere while the retry runs keeps it there.
      for (const type of USER_FOCUS_EVENTS) {
        document.addEventListener(type, cancel, true);
      }
    },
    settle(surface) {
      if (attempt?.surface === surface) attempt = { surface, settled: true };
    },
    claim(surface) {
      const claimed = attempt?.surface === surface && attempt.settled;
      if (claimed) cancel();
      return claimed;
    },
    retireSettled() {
      if (attempt?.settled === true) cancel();
    },
    cancel,
  };
}

/** One retry-focus hand-off for the component that owns a load and renders its error state. */
export function useRetryFocus(): RetryFocus {
  const [retryFocus] = useState(createRetryFocus);

  // Runs after every render of the owner, after its children's effects: a
  // failed retry's new surface has claimed by now, so what is left succeeded.
  useEffect(() => {
    retryFocus.retireSettled();
  });

  useEffect(() => retryFocus.cancel, [retryFocus]);

  return retryFocus;
}
